/**
 * Key rebinding: the conflict rule, and the code -> key-cap label mapping.
 *
 * ===========================================================================
 * THE CONFLICT RULE
 * ===========================================================================
 *
 * The obvious rule — "a key belongs to exactly one action, so binding it
 * anywhere strips it everywhere else" — is WRONG for this game, and it would
 * silently destroy the shipped defaults the moment a player touched the
 * screen. `DEFAULT_BINDINGS` deliberately binds KeyW to both `throttleForward`
 * and `pitchDown`, and KeyA to both `steerLeft` and `yawLeft`, because
 * `InputController.sample(airborne)` reads the ground set on the ground and
 * the air set in the air. Those two actions are never live at the same instant,
 * so they do not conflict.
 *
 * So conflicts are decided by CONTEXT OVERLAP. Every action is live on the
 * ground, in the air, or in both:
 *
 *     ground only : throttleForward/Reverse, steerLeft/Right
 *     air only    : pitchUp/Down, yawLeft/Right, rollLeft/Right
 *     both        : jump, boost, handbrake, ballCam
 *
 * Two actions conflict iff their context sets intersect. Binding a code to an
 * action removes that code from every action it conflicts with — LAST BIND
 * WINS — and leaves it alone everywhere else. `rollLeft`/`rollRight` are in the
 * air set even though ./controller also reads them on the ground; treating them
 * as air-only would let a player bind KeyQ to both `rollLeft` and
 * `throttleForward` and then wonder why reversing spins the car. Keeping them
 * in the air group makes the rule conservative in the direction that cannot
 * surprise anyone.
 *
 * ESCAPE IS NEVER BINDABLE. It is the only key guaranteed to get you out of the
 * bind prompt, and out of every menu screen. A player who bound it to `jump`
 * would have no way back.
 */

import {
  BINDABLE_ACTIONS,
  MAX_BINDING_SLOTS,
  cloneBindings,
  defaultBindings,
} from '../game/settings';
import type { KeyBindings } from '../input/controller';

export type BindableAction = keyof KeyBindings;

type Context = 'ground' | 'air';

const ACTION_CONTEXTS: Record<BindableAction, readonly Context[]> = {
  throttleForward: ['ground'],
  throttleReverse: ['ground'],
  steerLeft: ['ground'],
  steerRight: ['ground'],
  pitchUp: ['air'],
  pitchDown: ['air'],
  yawLeft: ['air'],
  yawRight: ['air'],
  rollLeft: ['air'],
  rollRight: ['air'],
  jump: ['ground', 'air'],
  boost: ['ground', 'air'],
  handbrake: ['ground', 'air'],
  ballCam: ['ground', 'air'],
};

/** Display order and labels for the controls screen. */
export const BINDING_ROWS: readonly {
  action: BindableAction;
  label: string;
  group: string;
}[] = [
  { action: 'throttleForward', label: 'Throttle', group: 'Driving' },
  { action: 'throttleReverse', label: 'Reverse', group: 'Driving' },
  { action: 'steerLeft', label: 'Steer Left', group: 'Driving' },
  { action: 'steerRight', label: 'Steer Right', group: 'Driving' },
  { action: 'jump', label: 'Jump', group: 'Driving' },
  { action: 'boost', label: 'Boost', group: 'Driving' },
  { action: 'handbrake', label: 'Powerslide / Air Roll', group: 'Driving' },
  { action: 'ballCam', label: 'Ball Cam', group: 'Driving' },
  { action: 'pitchUp', label: 'Pitch Up', group: 'Aerial' },
  { action: 'pitchDown', label: 'Pitch Down', group: 'Aerial' },
  { action: 'yawLeft', label: 'Yaw Left', group: 'Aerial' },
  { action: 'yawRight', label: 'Yaw Right', group: 'Aerial' },
  { action: 'rollLeft', label: 'Air Roll Left', group: 'Aerial' },
  { action: 'rollRight', label: 'Air Roll Right', group: 'Aerial' },
];

/** Keys the menu reserves for itself; offering them would trap the player. */
export const RESERVED_CODES: readonly string[] = ['Escape'];

export const isBindable = (code: string): boolean =>
  code.length > 0 && !RESERVED_CODES.includes(code);

/** True when two actions can be live at the same moment. */
export const actionsConflict = (a: BindableAction, b: BindableAction): boolean => {
  if (a === b) return false;
  return ACTION_CONTEXTS[a].some(ctx => ACTION_CONTEXTS[b].includes(ctx));
};

export interface BindResult {
  bindings: KeyBindings;
  /** Bindings dropped to resolve a conflict, for the "unbound X" toast. */
  displaced: { action: BindableAction; code: string }[];
  /** False when the code is reserved; `bindings` is then returned unchanged. */
  applied: boolean;
}

/**
 * Bind `code` to slot `slot` of `action`, returning a NEW binding map. Pure:
 * the caller decides when to commit, which is what lets the bind prompt be
 * cancelled without having already corrupted the live map.
 */
export const bindKey = (
  bindings: KeyBindings,
  action: BindableAction,
  slot: number,
  code: string
): BindResult => {
  if (!isBindable(code) || slot < 0 || slot >= MAX_BINDING_SLOTS) {
    return { bindings, displaced: [], applied: false };
  }

  const next = cloneBindings(bindings);
  const displaced: { action: BindableAction; code: string }[] = [];

  for (const other of BINDABLE_ACTIONS) {
    if (!actionsConflict(action, other)) continue;
    if (!next[other].includes(code)) continue;
    next[other] = next[other].filter(c => c !== code);
    displaced.push({ action: other, code });
  }

  // The same code twice on one action is a no-op that renders as a duplicate
  // key cap, so a rebind onto a slot the code already occupies just normalizes.
  const keys = next[action].filter(c => c !== code);
  while (keys.length < slot) keys.push('');
  keys[slot] = code;
  next[action] = keys.filter(c => c.length > 0).slice(0, MAX_BINDING_SLOTS);

  return { bindings: next, displaced, applied: true };
};

/** Clear one slot. An action may legitimately end up with no keys at all. */
export const clearBinding = (
  bindings: KeyBindings,
  action: BindableAction,
  slot: number
): KeyBindings => {
  const next = cloneBindings(bindings);
  next[action] = next[action].filter((_, i) => i !== slot);
  return next;
};

export const resetBindings = (): KeyBindings => defaultBindings();

/** Codes currently bound to something this action conflicts with. */
export const conflictingCodes = (
  bindings: KeyBindings,
  action: BindableAction
): ReadonlySet<string> => {
  const out = new Set<string>();
  for (const other of BINDABLE_ACTIONS) {
    if (!actionsConflict(action, other)) continue;
    for (const code of bindings[other]) out.add(code);
  }
  return out;
};

// ---------------------------------------------------------------------------
// Key caps
// ---------------------------------------------------------------------------

/**
 * `KeyboardEvent.code` -> what to print on the cap. Codes are physical, so they
 * are the right thing to STORE (a rebind survives a layout change), but they
 * are unreadable, so they are the wrong thing to SHOW. Only the irregular ones
 * are listed; everything else falls out of the three prefix rules below.
 */
const KEY_LABELS: Record<string, string> = {
  Space: 'SPACE',
  Enter: 'ENTER',
  NumpadEnter: 'NUM ⏎',
  Escape: 'ESC',
  Backspace: 'BKSP',
  Tab: 'TAB',
  CapsLock: 'CAPS',
  ShiftLeft: 'L SHIFT',
  ShiftRight: 'R SHIFT',
  ControlLeft: 'L CTRL',
  ControlRight: 'R CTRL',
  AltLeft: 'L ALT',
  AltRight: 'R ALT',
  MetaLeft: 'L META',
  MetaRight: 'R META',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  Minus: '−',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  Backquote: '`',
  ContextMenu: 'MENU',
  PageUp: 'PG UP',
  PageDown: 'PG DN',
  Insert: 'INS',
  Delete: 'DEL',
  Home: 'HOME',
  End: 'END',
};

export const keyLabel = (code: string): string => {
  if (!code) return '—';
  const known = KEY_LABELS[code];
  if (known) return known;
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return `NUM ${code.slice(6)}`;
  return code.toUpperCase();
};

/** Key caps for a row, always `MAX_BINDING_SLOTS` long so columns line up. */
export const slotLabels = (bindings: KeyBindings, action: BindableAction): string[] => {
  const keys = bindings[action];
  return Array.from({ length: MAX_BINDING_SLOTS }, (_, i) => keyLabel(keys[i] ?? ''));
};
